import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faTimes, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext, useWatch } from 'react-hook-form';
import toFixed from 'accounting-js/lib/toFixed.js';
import ReactTooltip from 'react-tooltip';

import Accordion, {
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
} from '../../../components/common/accordion';
import css from './animal-groups-list.module.css';
import {
  Option,
  SelectController,
} from '../../../components/form/elements/select';
import { TextInputComponent } from '../../../components/form/elements/text';
import { LabelComponent } from '../../../components/form/elements/label';
import { FormData } from './create';
import { DecimalInputComponent } from '../../../components/form/elements/decimal';
import InfoIcon from '../../../assets/icons/info.svg';

export default function AnimalGroupsList({
  name,
  period,
  animalGroupsClassifier,
  animalGroups,
  addNewGroup,
  deleteGroup,
  addFeedingstuff,
  deleteFeedingstuff,
  periods,
  formValues,
}: {
  name: string;
  period: string;
  animalGroupsClassifier: Option[];
  animalGroups: {
    [key: string]: {
      [key: string]: {
        feedingstuffs: { [key: string]: {} };
        feedingstuffsCount: number;
      };
    };
  };
  addNewGroup: Function;
  deleteGroup: Function;
  addFeedingstuff: Function;
  deleteFeedingstuff: Function;
  periods: string[];
  formValues?: FormData;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { getValues } = useFormContext();

  const getLDCAnimalsTotalCount = () => {
    const aGroups = getValues('animalGroups.'.concat(period));
    const valueArry =
      Object.keys(aGroups).length > 0
        ? Object.values(aGroups)
            .filter((animalGroup: any) => animalGroup.animal_count_ldc > 0)
            .map((animalGroup: any) => animalGroup.animal_count_ldc ?? 0)
        : null;

    return valueArry && valueArry.length > 0
      ? valueArry.reduce((a: number, b: number) => a + b)
      : 0;
  };

  const getAnimalsSetTotalCount = () => {
    const aGroups = getValues('animalGroups.'.concat(period));
    const valueArry =
      Object.keys(aGroups).length > 0
        ? Object.values(aGroups)
            .filter((animalGroup: any) => animalGroup.animal_count > 0)
            .map((animalGroup: any) => parseFloat(animalGroup.animal_count))
        : null;

    return valueArry && valueArry.length > 0
      ? valueArry.reduce((a: number, b: number) => a + b)
      : 0;
  };

  const [updateAnimalGroup, setUpdateAnimalGroup] = useState('');
  const [totalLDCAnimalsCount, setTotalLDCAnimalsCount] = useState(
    getLDCAnimalsTotalCount()
  );
  const [totalAnimalsSetCount, setTotalAnimalsSetCount] = useState(
    getAnimalsSetTotalCount()
  );

  const feedingstuffs = Object.values(formValues.feedingstuffs).map(
    (feedingstuff: any) => {
      const feedingstuffsClassifier = formValues.feedingstuffsClassifiers.find(
        (item) => item.value === feedingstuff.feedingstuff_id
      );
      return {
        value: feedingstuff.feedingstuff_id,
        label:
          feedingstuffsClassifier.label +
          ' (' +
          (feedingstuff.title?.value ?? feedingstuff.title) +
          ')',
        dry: feedingstuff.dry,
      } as Option;
    }
  );

  return (
    <div className={css['animal-groups-list']}>
      <div className={css['group-titles']}>
        <label>{tp('animal_group')}</label>
        <label>{tp('animal_count_from_ldc')}</label>
        <label>{tp('animal_count_for_calculations')}</label>
      </div>

      {Object.entries(animalGroups[period]).map(
        ([animalGroupKey, group]: [string, any], ind) => {
          return (
            <Accordion key={`${name}-${animalGroupKey}`}>
              <AccordionItem
                key={`${name}-${animalGroupKey}-item`}
                header={
                  <AccordionItemHeader
                    key={`${name}-${animalGroupKey}-item-header`}
                    chevronInRightSide={false}
                  >
                    <div
                      className={css['group-input'].concat(
                        ' ',
                        css['group-input-first']
                      )}
                    >
                      <AnimalGroupSelect
                        name={name}
                        animalGroupsClassifier={animalGroupsClassifier}
                        groupKey={animalGroupKey}
                        reversePeriod={
                          periods.filter((per) => per !== period)[0]
                        }
                        updateAnimalGroup={updateAnimalGroup}
                        setUpdateAnimalGroup={setUpdateAnimalGroup}
                        setTotalLDCAnimalsCount={() =>
                          setTotalLDCAnimalsCount(getLDCAnimalsTotalCount())
                        }
                        formValues={formValues}
                        period={period}
                      />
                    </div>
                    <div className={css['group-input']}>
                      <TextInputComponent
                        disabled={true}
                        name={name.concat(
                          '.',
                          animalGroupKey,
                          '.animal_count_ldc'
                        )}
                      />
                    </div>
                    <div className={css['group-input']}>
                      <AnimalCountSet
                        name={name.concat('.', animalGroupKey, '.animal_count')}
                        setTotalAnimalsSetCount={() => {
                          setTotalAnimalsSetCount(getAnimalsSetTotalCount());
                          setTotalLDCAnimalsCount(getLDCAnimalsTotalCount());
                        }}
                      />
                    </div>
                    {ind > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          deleteGroup(parseInt(animalGroupKey));
                          setTotalLDCAnimalsCount(getLDCAnimalsTotalCount());
                          setTotalAnimalsSetCount(getAnimalsSetTotalCount());
                          setUpdateAnimalGroup('');
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </AccordionItemHeader>
                }
                body={
                  <AccordionItemBody
                    key={`${name}-${animalGroupKey}-item-body`}
                  >
                    <FeedingstuffContent
                      key={`${name}-${animalGroupKey}-item-body-content`}
                      name={name}
                      period={period}
                      animalGroupKey={animalGroupKey}
                      feedingstuffOptions={feedingstuffs}
                      feedingstuffs={group.feedingstuffs}
                      addFeedingstuff={addFeedingstuff}
                      deleteFeedingstuff={deleteFeedingstuff}
                      periodDaysCount={formValues.periodsDaysCount[period]}
                    />
                  </AccordionItemBody>
                }
                opened={true}
                ignoreChildElementsOnOpen={true}
              />
            </Accordion>
          );
        }
      )}
      <div className={css['add-new-animal-group']}>
        <button type="button" onClick={() => addNewGroup()}>
          <FontAwesomeIcon icon={faPlus} /> {tp('add_more')}
        </button>
      </div>

      <div className={css['animal-groups-total']}>
        <label>{tp('total')}</label>
        <label>{totalLDCAnimalsCount}</label>
        <label>{totalAnimalsSetCount}</label>
      </div>
    </div>
  );
}

export function AnimalGroupSelect({
  name,
  animalGroupsClassifier,
  groupKey,
  reversePeriod,
  updateAnimalGroup,
  setUpdateAnimalGroup,
  setTotalLDCAnimalsCount,
  formValues,
  period,
}: {
  name: string;
  animalGroupsClassifier: Option[];
  groupKey: string;
  reversePeriod: string;
  updateAnimalGroup: string;
  setUpdateAnimalGroup: Function;
  setTotalLDCAnimalsCount: Function;
  formValues: FormData;
  period: string;
}) {
  const { setValue, getValues, trigger } = useFormContext();
  const [options, setOptions] = useState(animalGroupsClassifier);
  const animalGroupname = name.concat('.', groupKey, '.animal_group_id');
  const animalGroupWatch = useWatch({ name: animalGroupname });
  const dryCapacityPerDayWatch = useWatch({
    name: name.concat('.', groupKey, '.dry_intake_capacity_per_day'),
  });
  const currentValue = getValues(animalGroupname);

  useEffect(() => {
    if (dryCapacityPerDayWatch) {
      setValue(
        'animalGroups.'.concat(
          reversePeriod,
          '.',
          groupKey,
          '.dry_intake_capacity_per_day'
        ),
        dryCapacityPerDayWatch
      );
      trigger();
    }
  }, [dryCapacityPerDayWatch, groupKey, reversePeriod, setValue, trigger]);

  useEffect(() => {
    if (
      animalGroupWatch &&
      formValues.animalGroups[period][groupKey]?.animal_group_id !==
        animalGroupWatch
    ) {
      const dry = animalGroupsClassifier.find(
        (opt) => opt.value === animalGroupWatch
      );
      if (dry) {
        setValue(
          name.concat('.', groupKey, '.dry_intake_capacity_per_day'),
          dry.dry
        );
        setValue(
          'animalGroups.'.concat(
            reversePeriod,
            '.',
            groupKey,
            '.dry_intake_capacity_per_day'
          ),
          dry.dry
        );
      }
    }
  }, [
    animalGroupWatch,
    animalGroupsClassifier,
    reversePeriod,
    setValue,
    name,
    groupKey,
    formValues.animalGroups,
    period,
  ]);

  useEffect(() => {
    if (animalGroupWatch) {
      setValue(name.concat('.', groupKey, '.animal_count_ldc'), null);
      setValue(
        'animalGroups.'.concat(
          reversePeriod,
          '.',
          groupKey,
          '.animal_count_ldc'
        ),
        null
      );

      setUpdateAnimalGroup(animalGroupWatch);
      setValue(
        'animalGroups.'.concat(
          reversePeriod,
          '.',
          groupKey,
          '.animal_group_id'
        ),
        animalGroupWatch
      );

      setTotalLDCAnimalsCount();
    }
  }, [
    animalGroupWatch,
    animalGroupsClassifier,
    groupKey,
    name,
    reversePeriod,
    setUpdateAnimalGroup,
    setValue,
    setTotalLDCAnimalsCount,
    updateAnimalGroup,
  ]);

  useEffect(() => {
    if (updateAnimalGroup !== currentValue) {
      const usedAnimalGroups = Object.values(getValues(name))
        .filter(
          (animalGroup: any) => animalGroup.animal_group_id !== currentValue
        )
        .map((animalGroup: any) => {
          return animalGroup.animal_group_id;
        });
      setOptions(
        animalGroupsClassifier.filter(
          (opt) => !usedAnimalGroups.includes(opt.value)
        )
      );
    }
  }, [
    updateAnimalGroup,
    currentValue,
    getValues,
    animalGroupsClassifier,
    name,
  ]);

  return (
    <SelectController
      multi={false}
      asyncOptions={null}
      options={options}
      cacheOn={[]}
      disabled={false}
      name={name.concat('.', groupKey, '.animal_group_id')}
      parentName={null}
    />
  );
}

export function FeedingstuffContent({
  name,
  period,
  animalGroupKey,
  feedingstuffs,
  feedingstuffOptions,
  addFeedingstuff,
  deleteFeedingstuff,
  periodDaysCount,
}: {
  name: string;
  period: string;
  animalGroupKey: string;
  feedingstuffs: {};
  feedingstuffOptions: Option[];
  addFeedingstuff: Function;
  deleteFeedingstuff: Function;
  periodDaysCount: number;
}) {
  const { trigger } = useFormContext();
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const [feedingstuffExists, setFeedingstuffExists] = useState(
    Object.keys(feedingstuffs).length > 0
  );

  const [updateFeedingstuff, setUpdateFeedingstuff] = useState('');

  return (
    <>
      {feedingstuffExists && (
        <div className={css['feedingstuff-titles']}>
          <label className={css['feedingstuff-row']}>
            {tp('feedingstuff')}
          </label>
          <label className={css['feedingstuff-row']}>
            {tp('quantity_on_animal')}
          </label>
          <label className={css['feedingstuff-row']}>
            {tp('quantity_in_period_for_animal_group')}
          </label>
          <label className={css['feedingstuff-row-last']}>{tp('dry_kg')}</label>
        </div>
      )}
      <div className={css['feedingstuff-inputs']}>
        {feedingstuffs &&
          Object.keys(feedingstuffs).map(([feedingstuffKey]) => {
            return (
              <div
                key={`${name}-${animalGroupKey}-item-body-content-${feedingstuffKey}`}
              >
                <div className={css['remove-feedingstuff']}>
                  <button
                    type="button"
                    onClick={() => {
                      deleteFeedingstuff(
                        period,
                        animalGroupKey,
                        feedingstuffKey,
                        setFeedingstuffExists
                      );
                      setUpdateFeedingstuff('');
                      trigger();
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className={css['feedingstuff-row']}>
                  <FeedingstuffSelect
                    feedingstuffOptions={feedingstuffOptions}
                    feedingstuffKey={feedingstuffKey}
                    animalGroupName={name.concat('.', animalGroupKey)}
                    updateFeedingstuff={updateFeedingstuff}
                    setUpdateFeedingstuff={setUpdateFeedingstuff}
                  />
                </div>
                <div className={css['feedingstuff-row']}>
                  <AmountInDayOnAnimal
                    animalGroupName={name.concat('.', animalGroupKey)}
                    feedingstuffKey={feedingstuffKey}
                    periodDaysCount={periodDaysCount}
                  />
                </div>
                <div className={css['feedingstuff-row']}>
                  <LabelComponent
                    name={name.concat(
                      '.',
                      animalGroupKey,
                      '.feedingstuffs.',
                      feedingstuffKey,
                      '.amount_in_period_for_animal_group'
                    )}
                  />
                </div>
                <div className={css['feedingstuff-row-last']}>
                  <LabelComponent
                    name={name.concat(
                      '.',
                      animalGroupKey,
                      '.feedingstuffs.',
                      feedingstuffKey,
                      '.dry_kg'
                    )}
                  />
                </div>
              </div>
            );
          })}
        {feedingstuffs &&
          Object.keys(feedingstuffs).length < feedingstuffOptions.length && (
            <div className={css['add-feedingstuff']}>
              <button
                type="button"
                onClick={() => {
                  addFeedingstuff(period, animalGroupKey);
                  setFeedingstuffExists(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> {tp('add_feedingstuff')}
              </button>
            </div>
          )}
      </div>
      <div className={css[feedingstuffExists ? '' : 'hide']}>
        <div className={css['dry-titles']}>
          <label>
            {tp('dry_intake_capacity_per_day')}
            <img
              alt={''}
              data-tip
              data-for="dry_intake_capacity_per_day_info"
              src={InfoIcon}
            ></img>
            <ReactTooltip
              id="dry_intake_capacity_per_day_info"
              backgroundColor={'#002A58'}
              place="top"
              effect="solid"
              className={css['tooltip']}
            >
              {tp('dry_intake_capacity_per_day_info')}
            </ReactTooltip>
          </label>
          <label>{tp('dry_total_per_one_animal')}</label>
        </div>
        <div className={css['dry-inputs']}>
          <div className={css['dry-row']}>
            <DecimalInputComponent
              name={name.concat(
                '.',
                animalGroupKey,
                '.',
                'dry_intake_capacity_per_day'
              )}
              negative={false}
            />
          </div>
          <div className={css['dry-row']}>
            <LabelComponent
              name={name.concat(
                '.',
                animalGroupKey,
                '.dry_total_per_one_animal'
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export function FeedingstuffSelect({
  animalGroupName,
  feedingstuffKey,
  feedingstuffOptions,
  updateFeedingstuff,
  setUpdateFeedingstuff,
}: {
  animalGroupName: string;
  feedingstuffKey: string;
  feedingstuffOptions: Option[];
  updateFeedingstuff: string;
  setUpdateFeedingstuff: Function;
}) {
  const { getValues, setValue } = useFormContext();
  const [options, setOptions] = useState(feedingstuffOptions);
  const name = animalGroupName.concat(
    '.feedingstuffs.',
    feedingstuffKey,
    '.feedingstuff_id'
  );
  const feedingstuff = useWatch({ name: name });
  const amountInDayOnAnimal = useWatch({
    name: animalGroupName.concat(
      '.feedingstuffs.',
      feedingstuffKey,
      '.ammount_in_day_on_animal'
    ),
  });
  const currentValue = getValues(name);

  useEffect(() => {
    //set to update all other feedingstuff selects to remove used feedingstuffs
    if (feedingstuff && feedingstuff === currentValue) {
      setUpdateFeedingstuff(feedingstuff);
    }

    if (feedingstuff) {
      const nut = feedingstuffOptions.find((n) => n.value === feedingstuff);

      if (nut)
        setValue(
          animalGroupName.concat('.feedingstuffs.', feedingstuffKey, '.dry_kg'),
          amountInDayOnAnimal
            ? parseFloat(toFixed((amountInDayOnAnimal * nut.dry) / 100, 3))
            : null
        );
      setValue(
        animalGroupName.concat('.dry_total_per_one_animal'),
        toFixed(
          Object.values(getValues(animalGroupName.concat('.feedingstuffs')))
            .map((feedingstuff: any) => feedingstuff.dry_kg)
            .reduce((a: number, b: number) => a + b),
          2
        )
      );
    }
  }, [
    feedingstuff,
    amountInDayOnAnimal,
    animalGroupName,
    currentValue,
    getValues,
    feedingstuffOptions,
    feedingstuffKey,
    setUpdateFeedingstuff,
    setValue,
  ]);

  useEffect(() => {
    //set unused feedingstufs as options
    if (updateFeedingstuff !== currentValue) {
      const usedFeedingstuffs = Object.values(
        getValues(animalGroupName.concat('.feedingstuffs'))
      )
        .filter(
          (feedingstuff: any) => feedingstuff.feedingstuff_id !== currentValue
        )
        .map((feedingstuff: any) => feedingstuff.feedingstuff_id);
      setOptions(
        feedingstuffOptions.filter(
          (opt) => !usedFeedingstuffs.includes(opt.value)
        )
      );
    }
  }, [
    updateFeedingstuff,
    animalGroupName,
    currentValue,
    getValues,
    feedingstuffOptions,
  ]);

  return (
    <SelectController
      multi={false}
      asyncOptions={null}
      options={options}
      cacheOn={[]}
      disabled={false}
      name={name}
      parentName={null}
    />
  );
}

export function AmountInDayOnAnimal({
  animalGroupName,
  feedingstuffKey,
  periodDaysCount,
}: {
  animalGroupName: string;
  feedingstuffKey: string;
  periodDaysCount: number;
}) {
  const { setValue } = useFormContext();
  const name = animalGroupName.concat(
    '.feedingstuffs.',
    feedingstuffKey,
    '.ammount_in_day_on_animal'
  );
  const amount = useWatch({ name: name });
  const animalCount = useWatch({
    name: animalGroupName.concat('.animal_count'),
  });

  useEffect(() => {
    if (amount && animalCount !== '') {
      setValue(
        animalGroupName.concat(
          '.feedingstuffs.',
          feedingstuffKey,
          '.amount_in_period_for_animal_group'
        ),
        parseFloat(toFixed(animalCount * amount * periodDaysCount, 3))
      );
    } else {
      setValue(
        animalGroupName.concat(
          '.feedingstuffs.',
          feedingstuffKey,
          '.amount_in_period_for_animal_group'
        ),
        ''
      );
    }
  }, [
    animalCount,
    amount,
    animalGroupName,
    feedingstuffKey,
    periodDaysCount,
    setValue,
  ]);

  return <DecimalInputComponent name={name} negative={false} />;
}

export function AnimalCountSet({
  name,
  setTotalAnimalsSetCount,
}: {
  name: string;
  setTotalAnimalsSetCount: Function;
}) {
  const amount = useWatch({ name: name });
  useEffect(() => {
    setTotalAnimalsSetCount();
  }, [amount, setTotalAnimalsSetCount]);

  return <DecimalInputComponent name={name} negative={false} />;
}
